/* FLM — App root, ported from the prototype (Dating App/flm/app.jsx).
   Stage flow: splash → onboarding wizard → app (5 tabs + overlays).
   REAL DATA ONLY: outside Telegram the app renders the "Open in Telegram" gate;
   a failed session exchange renders a retry screen. The FLM_DATA mock layer
   survives exclusively behind the explicit ?demo=1 design-review flag. */
import React from 'react';
import { makeT, faDigits, applyI18nOverrides } from './lib/i18n';
import { isDemo } from './lib/demo';
import { FLM_DATA, LIKES_YOU } from './data/mock';
import { GlowBlob, BottomNav, Icon, PULSE_ENABLED, SparkBurst } from './components/ui';
import { Splash } from './screens/splash';
import { DiscoverScreen, MatchOverlay, ProfileDetail } from './screens/discover';
import { FeedScreen } from './screens/feed';
import { ProfileScreen } from './screens/profile';

/* Code-split the heavy / on-demand screens so the initial bundle stays lean:
   Onboarding + Map pull in Leaflet, Premium pulls in TON Connect + qrcode, Chat
   pulls in Socket.IO — none are needed for first paint of a returning user's deck. */
const Onboarding = React.lazy(() => import('./screens/onboarding').then((m) => ({ default: m.Onboarding })));
const MapScreen = React.lazy(() => import('./screens/map').then((m) => ({ default: m.MapScreen })));
const PremiumScreen = React.lazy(() => import('./screens/premium').then((m) => ({ default: m.PremiumScreen })));
const ChatsScreen = React.lazy(() => import('./screens/chat').then((m) => ({ default: m.ChatsScreen })));

/** Full-bleed loader shown while a lazy screen's chunk downloads. */
function LazyFallback() {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', zIndex: 70 }}>
      <span style={{ width: 34, height: 34, border: '3px solid var(--border-bright)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'flmSpinSlow 0.7s linear infinite' }} />
    </div>
  );
}
import { authTelegram, getMe, putLocation, markAuthReady, getI18nOverrides, getConversations, postSwipe, mediaUrl, getAppVersion, APP_VERSION, refreshSession, refreshTokenStored, setSession, accessToken, getSubscription, getRewards } from './api/client';
import { getRawInitData, getStartParam, readyAndExpand, getTelegramUser, requestLocation, haptic } from './lib/telegram';
import { playPing, unlockAudio } from './lib/sound';

/* Bump when the onboarding wizard changes shape — forces a fresh wizard run
   for stored sessions (server-side OnboardingState remains source of truth). */
const WIZARD_VERSION = '2';
if (localStorage.getItem('flm-wizard-v') !== WIZARD_VERSION) {
  localStorage.removeItem('flm-stage');
  localStorage.removeItem('flm-tab');
  localStorage.setItem('flm-wizard-v', WIZARD_VERSION);
}

/* splash logo gradient (same G1 the wizard uses) */
const G1 = 'linear-gradient(160deg, #7A1FA2, #2D0041 70%)';

/* ---------- Telegram gate — browser visits get pointed at the bot ---------- */
function TelegramGate({ t }) {
  return (
    <div data-screen-label="Telegram gate" style={{ position: 'absolute', inset: 0, background: 'var(--bg)', overflow: 'hidden', display: 'flex', flexDirection: 'column', zIndex: 80 }}>
      <GlowBlob color="is-purple" size={280} x="-10%" y="20%" o={0.8} />
      <GlowBlob color="is-mint" size={200} x="105%" y="70%" o={0.5} />
      <GlowBlob color="is-love" size={160} x="100%" y="34%" o={0.6} />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2, padding: '0 28px', textAlign: 'center' }}>
        <div style={{ position: 'relative', marginBottom: 34 }}>
          <span style={{ width: 96, height: 96, borderRadius: 32, background: G1, boxShadow: 'var(--glow-primary)', border: '1px solid rgba(122,31,162,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="heart" size={40} color="var(--accent)" fill="var(--accent)" style={{ filter: 'drop-shadow(0 0 14px rgba(255,212,145,.7))' }} />
          </span>
          <span style={{ position: 'absolute', top: -6, right: -6, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 16px var(--accent)' }}></span>
        </div>
        <div className="flm-display" style={{ fontSize: 54 }}>FLM</div>
        <div className="flm-mono" style={{ marginTop: 10, color: 'var(--text-2)' }}>{t('splashMonoRow')}</div>
        <p style={{ color: 'var(--text-2)', marginTop: 18, marginBottom: 0, fontSize: 16.5, lineHeight: 1.5, maxWidth: 280 }}>
          {t('gateBody')}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 22px 24px', position: 'relative', zIndex: 2 }}>
        <a className="flm-cta" href="https://t.me/FindLoveMateBot" style={{ textDecoration: 'none' }}>
          <Icon name="plane" size={18} fill="currentColor" color="none" />
          {t('gateCta')}
        </a>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Icon name="shield" size={13} color="var(--text-3)" />
          <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{t('splashPrivacy')}</span>
        </div>
        <span className="flm-mono" style={{ textAlign: 'center', fontSize: 8.5, color: 'var(--text-3)', paddingBottom: 6 }}>{t('gateHint')}</span>
      </div>
    </div>
  );
}

/* ---------- Auth error — initData present but the session exchange failed ---------- */
function AuthErrorScreen({ t, onRetry }) {
  return (
    <div data-screen-label="Sign-in error" style={{ position: 'absolute', inset: 0, background: 'var(--bg)', overflow: 'hidden', display: 'flex', flexDirection: 'column', zIndex: 80 }}>
      <GlowBlob color="is-love" size={260} x="50%" y="22%" o={0.55} />
      <GlowBlob color="is-purple" size={240} x="-10%" y="95%" o={0.7} />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2, padding: '0 28px', textAlign: 'center' }}>
        <span style={{ width: 84, height: 84, borderRadius: 28, background: 'var(--surface)', border: '1px solid var(--border-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
          <Icon name="shield" size={36} color="var(--love)" />
        </span>
        <h2 className="flm-display" style={{ fontSize: 30, color: 'var(--text)', margin: 0 }}>{t('authErrTitle')}</h2>
        <p style={{ color: 'var(--text-2)', marginTop: 14, marginBottom: 0, fontSize: 15.5, lineHeight: 1.5, maxWidth: 280 }}>
          {t('authErrBody')}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 22px 28px', position: 'relative', zIndex: 2 }}>
        <button className="flm-cta" onClick={onRetry}>{t('retry')}</button>
      </div>
    </div>
  );
}

/* ---------- New-version prompt — shown when the server has a newer build than
   the one this client loaded, so a deploy never leaves users on stale code. ---------- */
function UpdateBanner({ t }) {
  /* Full-screen dim scrim + a prominent bottom card — a new deploy means the
     loaded code is stale, so we firmly grab attention (dark backdrop, blur,
     accent glow) and make Refresh the one clear action. */
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 96, display: 'flex', flexDirection: 'column',
      justifyContent: 'flex-end', background: 'rgba(0,0,0,0.72)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      animation: 'flmFadeIn 0.22s ease',
    }}>
      <div style={{ padding: '0 16px max(20px, var(--safe-bottom))', animation: 'flmSlideUp 0.34s var(--ease-spring)' }}>
        <div className="flm-glass" style={{
          padding: 20, borderRadius: 22, border: '1px solid var(--accent)',
          boxShadow: 'var(--glow-accent), var(--shadow-offer)', textAlign: 'center',
        }}>
          <span style={{
            width: 56, height: 56, borderRadius: 18, background: 'var(--accent-tint)',
            border: '1px solid var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 14, boxShadow: 'var(--glow-accent)',
          }}>
            <Icon name="rewind" size={26} color="var(--accent)" />
          </span>
          <div className="flm-display" style={{ fontSize: 22, color: 'var(--text)', marginBottom: 6 }}>{t('updateTitle')}</div>
          <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 18, lineHeight: 1.45 }}>{t('updateBody')}</div>
          <button className="flm-cta" style={{ width: '100%' }} onClick={() => window.location.reload()}>
            {t('updateCta')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Celebratory reward popup — fires for a granted referral / trial reward (premium
   days or sparks). A spark-burst "cheer" behind a scale-in card; the copy reflects
   the actual reward from settings. Tap anywhere / the button to dismiss. */
function RewardPopup({ reward, t, faNum, onClose }) {
  const isTrial = reward?.kind === 'trial';
  const isPremium = (reward?.days ?? 0) > 0;
  const headline = isPremium
    ? t('rewardPremium', { n: faNum(reward.days), plan: reward.planName || t('premiumBadge') })
    : t('rewardSparks', { n: faNum(reward?.sparks ?? 0) });
  return (
    <div onClick={onClose} style={{
      position: 'absolute', inset: 0, zIndex: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 30px', background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      animation: 'flmFadeIn 0.2s ease',
    }}>
      {/* cheer burst behind the card */}
      <div style={{ position: 'absolute', top: '38%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
        <SparkBurst size={320} />
      </div>
      <div onClick={(e) => e.stopPropagation()} className="flm-hairline" style={{
        position: 'relative', width: '100%', maxWidth: 320, borderRadius: 24, padding: '30px 24px 22px',
        textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        background: 'radial-gradient(120% 120% at 50% 0%, rgba(255,212,145,0.18), transparent 55%), var(--surface)',
        border: '1px solid rgba(255,212,145,0.5)', boxShadow: '0 0 60px rgba(255,212,145,0.25), var(--shadow-card)',
        animation: 'flmSlideUp 0.4s var(--ease-spring)',
      }}>
        <div style={{ fontSize: 46, lineHeight: 1 }}>{isPremium ? '🎉' : '✨'}</div>
        <span className="flm-mono" style={{ fontSize: 9, color: 'var(--accent)' }}>
          {isTrial ? t('rewardTrialTag') : t('rewardInviteTag')}
        </span>
        <b className="flm-display" style={{ fontSize: 24, color: 'var(--text)', lineHeight: 1.15 }}>{headline}</b>
        <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>
          {isPremium ? t('rewardPremiumSub') : t('rewardSparksSub')}
        </span>
        <button className="flm-cta" style={{ marginTop: 14, width: '100%' }} onClick={onClose}>{t('rewardCta')}</button>
      </div>
    </div>
  );
}

export function App() {
  // Telegram identity (name, language, photo) comes free with the launch payload.
  const tgUser = React.useMemo(() => getTelegramUser(), []);
  const demo = React.useMemo(() => isDemo(), []);
  const initData = React.useMemo(() => getRawInitData(), []);
  // Referral token from the launch deep-link (?startapp=ref_<id>); sent on first auth.
  const startParam = React.useMemo(() => getStartParam(), []);
  const inTelegram = Boolean(initData);

  const [lang, setLang] = React.useState(
    () => localStorage.getItem('flm-lang') || (tgUser?.languageCode === 'fa' ? 'fa' : 'en'),
  );
  const [i18nRev, setI18nRev] = React.useState(0);
  const t = React.useMemo(() => makeT(lang), [lang, i18nRev]);
  const faNum = React.useMemo(() => (lang === 'fa' ? faDigits : (x) => String(x)), [lang]);

  /* Admin-managed translation overrides (public endpoint; silent fallback). */
  React.useEffect(() => {
    getI18nOverrides(lang)
      .then((entries) => {
        if (applyI18nOverrides(lang, entries)) setI18nRev((v) => v + 1);
      })
      .catch(() => {});
  }, [lang]);

  /* New-version watcher: poll the server's build version and, when it differs
     from the build this client loaded, surface the refresh banner. Only fires
     when BOTH versions are real (non-'dev'), so local/demo never prompts. Checks
     on an interval + whenever the app regains focus (returning to the webview). */
  React.useEffect(() => {
    if (APP_VERSION === 'dev') return; // local/demo build — no deploy cycle
    let stop = false;
    const check = () => {
      if (document.visibilityState === 'hidden') return;
      getAppVersion().then((v) => {
        if (!stop && v && v !== 'dev' && v !== APP_VERSION) setUpdateAvailable(true);
      });
    };
    check();
    const id = setInterval(check, 90_000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => { stop = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
  }, []);

  const [stage, setStage] = React.useState(() => localStorage.getItem('flm-stage') || 'splash');
  const [meName, setMeName] = React.useState(
    () => localStorage.getItem('flm-name') || tgUser?.firstName || 'Sam',
  );
  /* my own cover photo — used by the "It's mutual" overlay's You tile */
  const [meCover, setMeCover] = React.useState(null);
  const [tab, setTab] = React.useState(() => {
    const saved = localStorage.getItem('flm-tab') || 'discover';
    // Pulse is hidden — never strand a returning user on a tab the nav can't reach.
    return saved === 'feed' && !PULSE_ENABLED ? 'discover' : saved;
  });
  const [premium, setPremium] = React.useState(() => localStorage.getItem('flm-premium') === '1');
  const [discoverMode, setDiscoverMode] = React.useState(() => localStorage.getItem('flm-discover-mode') || 'deck');
  const [match, setMatch] = React.useState(null);
  const [premiumOpen, setPremiumOpen] = React.useState(false);
  /* When a LOCKED premium feature opens the upgrade screen, this carries the plan
     id that unlocks it (e.g. 'infinity' for message-before-match, 'spark' for
     rewind) so the Premium screen can highlight/scroll to that tier. null = no
     specific target (generic upsell). */
  const [premiumTarget, setPremiumTarget] = React.useState(null);
  /* onUpgrade(tier?) — callers that pass a tier point the Premium screen at the
     plan that unlocks the locked feature; calling with no arg keeps the generic
     ladder (tier=null → no highlight). */
  const onUpgrade = React.useCallback((tier) => { setPremiumTarget(tier ?? null); setPremiumOpen(true); }, []);
  const [openChatId, setOpenChatId] = React.useState(null);
  const [mapProfile, setMapProfile] = React.useState(null);
  /* 'pending' | 'ok' | 'error' — only meaningful inside Telegram without ?demo=1 */
  const [authState, setAuthState] = React.useState('pending');
  /* new-deploy detector: poll the server build version; prompt refresh on change */
  const [updateAvailable, setUpdateAvailable] = React.useState(false);

  React.useEffect(() => { localStorage.setItem('flm-lang', lang); }, [lang]);
  React.useEffect(() => { localStorage.setItem('flm-stage', stage); }, [stage]);
  React.useEffect(() => { localStorage.setItem('flm-tab', tab); }, [tab]);
  React.useEffect(() => { localStorage.setItem('flm-premium', premium ? '1' : '0'); }, [premium]);
  React.useEffect(() => { localStorage.setItem('flm-discover-mode', discoverMode); }, [discoverMode]);

  /* Telegram session — REFRESH-TOKEN-FIRST lifecycle (tokens persist in
     localStorage, surviving reload AND a full close/reopen):

       a. Stored refresh token? → refreshSession() (rotate → fresh access).
          Success skips initData entirely — the reload/reopen happy path.
       b. Else (or refresh produced no usable session) AND initData present →
          authTelegram(initData) → store the minted session.
       c. Both fail → retry screen.

     Telegram initData is single-use server-side, so re-sending it on a reload
     or Retry 401s with "initData already used" (the old lockout). With a stored
     session we NEVER re-send it — and never surface that replay error. A getMe
     network hiccup keeps the tokens and shows Retry, which re-runs this flow
     (refresh → getMe) and succeeds once the network returns. */
  const runAuth = React.useCallback(() => {
    setAuthState('pending');

    const establish = async () => {
      // (a) Reload/reopen happy path: a stored refresh token recovers the
      // session without touching the single-use initData.
      if (refreshTokenStored() && (await refreshSession())) return true;
      // also covers the case where refresh kept a still-valid access token
      if (accessToken()) {
        markAuthReady();
        return true;
      }
      // (b) No usable session → mint one from initData (first launch).
      if (initData) {
        const res = await authTelegram(initData, startParam);
        setSession({ accessToken: res.accessToken, refreshToken: res.refreshToken, userId: res.user.id });
        markAuthReady(); // unblocks queued wizard/location calls
        return true;
      }
      return false; // no refresh token and no initData — nothing to do
    };

    establish()
      .then((ok) => {
        if (!ok) {
          // No session and no initData (e.g. outside Telegram) — surface retry.
          setAuthState('error');
          return undefined;
        }
        markAuthReady();
        return getMe();
      })
      .then((me) => {
        if (me === undefined) return; // already handled (no session)
        if (me?.onboarding?.completed) {
          if (me.profile?.displayName) {
            setMeName(me.profile.displayName);
            localStorage.setItem('flm-name', me.profile.displayName);
          }
          // my cover photo for the match overlay's You tile (cover first, else first)
          const photos = me.profile?.photos ?? [];
          const cover = photos.find((p) => p.isCover) ?? photos[0];
          if (cover?.url) setMeCover(mediaUrl(cover.url));
          // premium is SERVER truth — an admin grant must unlock premium UI
          // (e.g. the real "who likes you" list) on any device, not just after
          // an in-session purchase. localStorage was making granted users still
          // see the free paywall.
          const isPrem = Boolean(me.user?.isPremium);
          setPremium(isPrem);
          localStorage.setItem('flm-premium', isPrem ? '1' : '0');
          setStage('app');
          // refresh coarse location in the background (Telegram LocationManager first)
          requestLocation().then((loc) => {
            if (loc) putLocation(loc.lat, loc.lng, loc.accuracy).catch(() => {});
          });
        } else {
          // Server says NOT onboarded (fresh user, or account deleted/reset in
          // admin) — stale local state must not skip the wizard.
          localStorage.removeItem('flm-stage');
          localStorage.removeItem('flm-tab');
          setStage((s) => (s === 'app' ? 'splash' : s));
        }
        setAuthState('ok');
      })
      .catch(() => {
        // KEEP the session on a transient getMe failure (network blip). Retry
        // re-runs runAuth → refreshSession → getMe, which works once the network
        // is back. A genuinely dead access token already triggered a single
        // refresh inside authed(); if THAT failed, the session was cleared there
        // and the retry falls back to initData. Either way we never replay
        // single-use initData while a working session exists.
        setAuthState('error');
      });
  }, [initData]);

  React.useEffect(() => {
    readyAndExpand();
    // Boot the auth flow when there's anything to authenticate with: a stored
    // refresh token (reload/reopen) OR fresh initData (first launch). Pure
    // browser visits with neither fall through to the Telegram gate.
    if (!initData && !refreshTokenStored()) return;
    runAuth();
  }, [initData, runAuth]);

  /* Nav badge: real unread total from live conversations (demo keeps the mock sum).
     Re-checked on tab switches (reading a thread clears its unread) + a slow poll. */
  const [unreadLive, setUnreadLive] = React.useState(0);
  React.useEffect(() => {
    if (demo || !inTelegram || stage !== 'app' || authState !== 'ok') return undefined;
    let on = true;
    const load = () => {
      getConversations()
        .then((res) => {
          if (on && Array.isArray(res?.items)) {
            setUnreadLive(res.items.reduce((s, c) => s + (c.unread || 0), 0));
          }
        })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { on = false; clearInterval(iv); };
  }, [demo, inTelegram, stage, tab, authState]);

  const unread = demo ? FLM_DATA.chats.reduce((s, c) => s + c.unread, 0) : unreadLive;

  /* Entitlements for the MAP profile overlay's limit gate (the Discover deck has
     its own copy). Fetched on sign-in + refreshed whenever a map profile opens,
     so tapping like/spark there gates to the paywall exactly like the deck. */
  const [mapSub, setMapSub] = React.useState(null);
  const refreshMapSub = React.useCallback(() => {
    if (demo || !inTelegram) return;
    getSubscription().then((r) => setMapSub(r || null)).catch(() => {});
  }, [demo, inTelegram]);
  React.useEffect(() => {
    if (stage === 'app' && authState === 'ok') refreshMapSub();
  }, [stage, authState, refreshMapSub]);
  React.useEffect(() => { if (mapProfile) refreshMapSub(); }, [mapProfile, refreshMapSub]);

  // Celebratory rewards (referral / trial) queued server-side → show a popup the
  // next time the user is in the app. Read-once (the GET clears them).
  const [rewards, setRewards] = React.useState([]);
  React.useEffect(() => {
    if (demo || !inTelegram || stage !== 'app' || authState !== 'ok') return;
    getRewards().then((r) => setRewards(r?.items || [])).catch(() => {});
  }, [demo, inTelegram, stage, authState]);
  // Exhausted = the DRIP BUCKET balance is 0 (not today's consumption count), so
  // bonus charges (streak / referral / recharge) are usable. Falls back to the
  // legacy usage math only if a pre-drip API omits `recharge`.
  const mapEnt = mapSub?.entitlements;
  const mapRc = mapSub?.recharge;
  const mapUsage = mapSub?.usage || {};
  const mapLikesExhausted = mapRc
    ? mapRc.like != null && (Number(mapRc.like.balance) || 0) <= 0
    : typeof mapEnt?.likesPerDay === 'number' && (Number(mapUsage.yesToday) || 0) >= mapEnt.likesPerDay;
  const mapSparksExhausted = mapRc
    ? !!mapRc.spark && (Number(mapRc.spark.balance) || 0) <= 0
    : typeof mapEnt?.superLikesPerDay === 'number' && (Number(mapUsage.sparkToday) || 0) >= mapEnt.superLikesPerDay;

  /* Unlock the Web Audio context on the first user gesture so the in-app
     notification chime is allowed to play later (browser autoplay policy). */
  React.useEffect(() => {
    const onGesture = () => unlockAudio();
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('touchstart', onGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('touchstart', onGesture);
    };
  }, []);

  /* Live chat notification — sound + haptic when a message arrives while the
     user is NOT on the chats tab (on the tab, the list/thread shows it live, so
     a ping there would be noise). `conversation:updated` hits the recipient's
     user room for every inbound message; `request:new` for a pre-match request.
     Refs read the latest tab without re-subscribing the socket each switch. */
  const tabRef = React.useRef(tab);
  tabRef.current = tab;
  React.useEffect(() => {
    if (demo || !inTelegram || stage !== 'app' || authState !== 'ok') return undefined;
    // Dynamic import keeps socket.io-client OUT of the main bundle (lazy chunk,
    // loaded after sign-in) — the chat screen reuses the same socket singleton.
    let s = null;
    let onIncoming = null;
    let cancelled = false;
    import('./lib/realtime').then(({ getSocket }) => {
      if (cancelled) return;
      s = getSocket();
      if (!s) return;
      onIncoming = () => {
        if (tabRef.current === 'chats') return; // engaged with chats — no ping
        setUnreadLive((n) => n + 1); // optimistic badge bump (20s poll reconciles)
        playPing();
        haptic('success');
      };
      s.on('conversation:updated', onIncoming);
      s.on('request:new', onIncoming);
    });
    return () => {
      cancelled = true;
      if (s && onIncoming) {
        s.off('conversation:updated', onIncoming);
        s.off('request:new', onIncoming);
      }
    };
  }, [demo, inTelegram, stage, authState]);

  const onMatchMessage = () => {
    // demo: jump straight into the mock thread; live: land on the chats list
    const c = demo && match ? FLM_DATA.chats.find((x) => x.with === match.id) : null;
    setMatch(null);
    setTab('chats');
    if (c) setOpenChatId(c.id);
  };

  /* Map profile overlay actions — live cards record the swipe; demo keeps LIKES_YOU. */
  const mapDecide = (p, decision) => {
    if (!demo && p?.userId) {
      postSwipe(p.userId, decision)
        .then((res) => { if (res?.match) setMatch(p); })
        // a daily-limit refusal routes to the paywall (mirrors the deck); other
        // errors stay silent. Refresh entitlements so the gate reflects the spend.
        .catch((err) => { if (/limit/i.test(err?.message || '')) onUpgrade(); })
        .finally(() => { if (decision !== 'pass') refreshMapSub(); });
    } else if (demo && decision !== 'pass' && LIKES_YOU.has(p.id)) {
      setMatch(p);
    }
  };

  /* Hard gates (real-data mode only) */
  const gated = !demo && !inTelegram;
  const authFailed = !demo && inTelegram && authState === 'error';

  return (
    /* Phone-frame: the design is a 390–430px phone layout. Inside Telegram the
       viewport already matches; in a desktop browser we center a phone-width
       column instead of stretching edge-to-edge. */
    <div style={{ height: '100dvh', background: '#000', display: 'flex', justifyContent: 'center' }}>
    <div dir={lang === 'fa' ? 'rtl' : 'ltr'}
      style={{ position: 'relative', height: '100dvh', width: '100%', maxWidth: 430, background: '#000', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: lang === 'fa' ? 'var(--font-fa)' : 'var(--font-body)', borderInline: '1px solid var(--border)' }}>

      {/* ambient glow */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <GlowBlob color="is-purple" size={300} x="-10%" y="105%" o={0.55} />
      </div>

      {gated ? <TelegramGate t={t} /> : authFailed ? <AuthErrorScreen t={t} onRetry={runAuth} /> : (
        <React.Fragment>
          {stage === 'app' ? (
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, zIndex: 1 }}>
              {/* clears the status bar + Telegram's top controls (fullscreen) */}
              <div style={{ height: 'max(6px, var(--safe-top))' }}></div>
              {/* keyed wrapper → a soft crossfade on every tab switch. Screens already
                  mount/unmount per tab, so the key adds no extra lifecycle cost. */}
              <div key={tab} className="flm-tabview" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {tab === 'discover' ? <DiscoverScreen t={t} faNum={faNum} premium={premium}
                  mode={discoverMode} setMode={setDiscoverMode}
                  onMatch={setMatch} onUpgrade={onUpgrade} /> : null}
                {tab === 'feed' ? <FeedScreen t={t} faNum={faNum} onOpenProfile={() => {}} /> : null}
                {tab === 'map' ? <React.Suspense fallback={<LazyFallback />}><MapScreen t={t} faNum={faNum} onOpenProfile={setMapProfile} onUpgrade={onUpgrade} /></React.Suspense> : null}
                {tab === 'chats' ? <React.Suspense fallback={<LazyFallback />}><ChatsScreen t={t} faNum={faNum} openChatId={openChatId} onOpenChat={setOpenChatId} onCloseChat={() => setOpenChatId(null)} onUpgrade={onUpgrade} /></React.Suspense> : null}
                {tab === 'profile' ? <ProfileScreen t={t} faNum={faNum} lang={lang} setLang={setLang}
                  premium={premium} onUpgrade={onUpgrade} meName={meName} /> : null}
              </div>
              <BottomNav tab={tab} setTab={(x) => { if (x !== tab) haptic('select'); setTab(x); setOpenChatId(null); }} t={t} unread={unread} />
            </div>
          ) : null}

          {stage === 'splash' ? <Splash t={t} lang={lang} setLang={setLang} onDone={() => setStage('onboarding')} /> : null}
          {stage === 'onboarding' ? <React.Suspense fallback={<LazyFallback />}><Onboarding t={t} lang={lang} faNum={faNum} tgUser={tgUser} onDone={(name) => { setMeName(name); localStorage.setItem('flm-name', name); setStage('app'); }} /></React.Suspense> : null}

          {/* global overlays */}
          {match ? <MatchOverlay profile={match} meName={meName} mePhoto={meCover} t={t} onClose={() => setMatch(null)} onMessage={onMatchMessage} /> : null}
          {premiumOpen ? <React.Suspense fallback={<LazyFallback />}><PremiumScreen t={t} faNum={faNum} variant="ladder" target={premiumTarget} onClose={() => { setPremiumOpen(false); setPremiumTarget(null); }} onPurchased={() => { setPremium(true); setPremiumOpen(false); setPremiumTarget(null); }} /></React.Suspense> : null}
          {/* Profile opened from the map. ProfileDetail plays its shrink animation
              (closing=true) then fires the action — so each handler must DISMISS the
              overlay afterwards (back to the map; a match still pops MatchOverlay).
              Without the dismiss the faded-out card stayed mounted → black screen. */}
          {mapProfile ? <ProfileDetail profile={mapProfile} t={t} faNum={faNum}
            onUpgrade={(tier) => { setMapProfile(null); onUpgrade(tier); }}
            likesExhausted={mapLikesExhausted} sparksExhausted={mapSparksExhausted}
            onClose={() => setMapProfile(null)}
            onPass={(p) => { mapDecide(p, 'pass'); setMapProfile(null); }}
            onLike={(p) => { mapDecide(p, 'yes'); setMapProfile(null); }}
            onSpark={(p) => { mapDecide(p, 'spark'); setMapProfile(null); }} /> : null}
        </React.Fragment>
      )}

      {/* celebratory reward popup (referral / trial) — one at a time */}
      {rewards.length > 0 ? (
        <RewardPopup reward={rewards[0]} t={t} faNum={faNum} onClose={() => setRewards((r) => r.slice(1))} />
      ) : null}

      {/* new-version refresh prompt — above everything, any stage */}
      {updateAvailable ? <UpdateBanner t={t} /> : null}

      {/* film grain */}
      <div className="grain-overlay" style={{ position: 'absolute' }}></div>
    </div>
    </div>
  );
}
